import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Plus, Trash2 } from "lucide-react";
import { supabase, formatSupabaseError } from "../lib/supabase";
import { DEFAULT_IMPRESSUM, DEFAULT_DATENSCHUTZ, DEFAULT_AGB } from "../lib/legalDefaults";
import ImageUpload from "./ImageUpload";

const DEFAULT_CONTENT = {
  hero: { eyebrow: "Independent Music Label", line1: "STRAßENWERT.", line2: "Musik mit Straßenwert.", tagline: "Wir bauen keine Rapper. Wir bauen Marken.", cta_primary: "Jetzt bewerben", cta_secondary: "Unsere Leistungen" },
  stats: [
    { label: "Künstler im Aufbau", value: "4", note: "Aktives Roster" },
    { label: "Releases in Arbeit", value: "13", note: "Q1 / Q2 Pipeline" },
    { label: "Netzwerk Partner", value: "52", note: "DSPs · Medien · Brands" },
    { label: "Content Output", value: "320+", note: "Visuals / Jahr" },
  ],
  about: { eyebrow: "01 — Über Uns", title: "", intro: "", pillars: [], image_url: "", caption_eyebrow: "", caption_text: "" },
  services: [],
  artists: [],
  creators: [
    {
      name: "SCHABROWSKI",
      slug: "schabrowski",
      tag: "Content Creator · Berlin",
      role: "",
      image: "",
      instagram_handle: "@real.schabrowski",
      instagram_url: "https://www.instagram.com/real.schabrowski/",
      youtube_label: "Aktuelle Videos",
      youtube_url: "",
      impressum_name: "Marino Rogait",
      impressum_address: "Biedenkopfer Straße 54",
      impressum_zip_city: "13507 Berlin",
      impressum_country: "Deutschland",
      impressum_email: "schabrowski@strassenwert.de",
      impressum: "",
      impressum_updated: "",
    },
  ],
  producers: [],
  releases: [],
  release_meta: { eyebrow: "Now Playing — Featured Drops", title_a: "Aktuelle", title_b: "Releases.", empty_title_a: "Wir arbeiten", empty_title_b: "an der Musik.", intro: "", empty_intro: "", empty_badge: "Coming Soon", empty_headline_a: "Wir arbeiten", empty_headline_b: "an der Musik.", empty_body: "", empty_status: "In Production", empty_eta: "TBA" },
  studios: { eyebrow: "05 — Unsere Studios", title_a: "Coming Soon.", title_b: "Die Räume", title_c: "entstehen.", intro: "", build_year: "2026", phase: "01/03", location: "BLN", launch: "TBA", handles: [] },
  studio_page: {
    eyebrow: "Mobile Studio · Berlin & Umgebung",
    title_a: "Wir kommen",
    title_b: "zu dir.",
    intro: "Unser festes Studio ist im Aufbau — bis dahin packen wir alles ein und kommen zu dir. Recording-Stack, Interface, Mikrofone, Akustik — komplette Session bei dir zu Hause. Mix & Master folgen Inhouse.",
    services_title: "Was wir machen.",
    services_intro: "Preise sind Startpunkte. Mix & Master sind Add-Ons — Pricing individuell je nach Umfang.",
    form_title: "Wann passt's bei dir?",
    form_intro: "Wir kommen mit komplettem Equipment vorbei. Sag uns wann und wo — wir melden uns innerhalb 48h.",
    services: [
      { code: "01", title: "Aufnahme", price: "ab 49 €", sub: "/ Session", description: "Recording-Session bei dir vor Ort. Inkl. Mikrofon, Interface, Laptop und allem was wir brauchen. Vocal-Booth & Akustik richten sich nach den Gegebenheiten bei dir.", cta: "Session anfragen" },
      { code: "02", title: "Mix", price: "ab 89 €", sub: "/ Song", description: "Professioneller Mix Inhouse. Stems aufnehmen wir vor Ort oder du schickst sie zu.", cta: "Mix anfragen" },
      { code: "03", title: "Master", price: "ab 39 €", sub: "/ Song", description: "Mastering Inhouse — Loudness, Druck, finale Politur fürs Streaming.", cta: "Master anfragen" },
      { code: "04", title: "Mix + Master", price: "ab 119 €", sub: "/ Song", description: "Mix und Master als Paket. Eine Sound-Signature, ein klarer Preis.", cta: "Mix + Master anfragen" },
      { code: "05", title: "Beats", price: "Auf Anfrage", sub: "/ individuell", description: "Maßgeschneiderte Beats aus unserem Producer-Roster. Pricing nach Umfang und Nutzungsrechten.", cta: "Beat anfragen" },
    ],
  },
  contact: { email: "info@strassenwert.de", phone: "+49 175 50 03 19 2", phone_link: "+491755003192", location: "Berlin · Studios im Aufbau", intro: "" },
  story: {
    eyebrow: "Die Geschichte hinter Straßenwert",
    title_a: "Vom Block",
    title_b: "ins Studio.",
    subtitle: "",
    portrait_url: "https://customer-assets.emergentagent.com/job_neon-beats-label/artifacts/2powx47n_image.png",
    author_name: "Maurizio Rogait",
    author_role: "Gründer",
    quote: "",
    quote_attribution: "",
    body: "",
    signoff: "Mit Liebe & Hustle",
  },
};

export const ContentEditor = ({ content: initial, onSaved }) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initial) setDraft(JSON.parse(JSON.stringify(initial)));
  }, [initial]);

  if (!draft) return <div className="text-zinc-500 text-sm">Lade Inhalte…</div>;

  const update = (path, value) => {
    setDraft((d) => {
      const clone = JSON.parse(JSON.stringify(d));
      let ref = clone;
      for (let i = 0; i < path.length - 1; i++) {
        if (ref[path[i]] === undefined || ref[path[i]] === null) {
          ref[path[i]] = typeof path[i + 1] === "number" ? [] : {};
        }
        ref = ref[path[i]];
      }
      ref[path[path.length - 1]] = value;
      return clone;
    });
  };

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("site_content")
      .update({ content: draft, updated_at: new Date().toISOString() })
      .eq("id", 1);
    setSaving(false);
    if (error) {
      toast.error(formatSupabaseError(error));
      return;
    }
    toast.success("Änderungen gespeichert.");
    onSaved?.();
  };

  const resetAll = async () => {
    if (!window.confirm("Alle Inhalte auf Standard zurücksetzen? Deine Änderungen gehen verloren.")) return;
    const { error } = await supabase
      .from("site_content")
      .update({ content: DEFAULT_CONTENT, updated_at: new Date().toISOString() })
      .eq("id", 1);
    if (error) {
      toast.error(formatSupabaseError(error));
      return;
    }
    setDraft(DEFAULT_CONTENT);
    toast.success("Standard wiederhergestellt.");
    onSaved?.();
  };

  return (
    <div>
      <div className="sticky top-[154px] z-30 -mx-6 md:-mx-10 px-6 md:px-10 py-4 mb-8 bg-[#050505]/90 backdrop-blur-xl border-y border-white/[0.06] flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
            <span className="red-led" /> Inhalte bearbeiten
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetAll} className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-white/30 text-zinc-400 hover:text-white" data-testid="content-reset">
            <RotateCcw size={12} /> Reset
          </button>
          <button onClick={save} disabled={saving} data-testid="content-save" className="group relative inline-flex items-center gap-3 bg-white text-black px-6 py-3 text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden disabled:opacity-50 hover:shadow-[0_0_24px_rgba(255,0,51,0.35)] transition-shadow">
            <span className="relative z-10 group-hover:text-white transition-colors">{saving ? "Speichere…" : "Speichern"}</span>
            <Save size={14} className="relative z-10 group-hover:text-white transition-colors" />
            <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
          </button>
        </div>
      </div>

      <Section title="Hero">
        <Field label="Eyebrow" value={draft.hero?.eyebrow} onChange={(v) => update(["hero", "eyebrow"], v)} testid="field-hero-eyebrow" />
        <Field label="Zeile 1 (Haupt-Headline)" value={draft.hero?.line1} onChange={(v) => update(["hero", "line1"], v)} testid="field-hero-line1" />
        <Field label="Zeile 2 (Sub-Headline)" value={draft.hero?.line2} onChange={(v) => update(["hero", "line2"], v)} testid="field-hero-line2" />
        <Field label="Tagline" textarea value={draft.hero?.tagline} onChange={(v) => update(["hero", "tagline"], v)} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="CTA Primary" value={draft.hero?.cta_primary} onChange={(v) => update(["hero", "cta_primary"], v)} />
          <Field label="CTA Secondary" value={draft.hero?.cta_secondary} onChange={(v) => update(["hero", "cta_secondary"], v)} />
        </div>
      </Section>

      <Section title="Stats (Hero Cards)">
        <div className="grid md:grid-cols-2 gap-4">
          {(draft.stats || []).map((s, i) => (
            <div key={i} className="border border-white/[0.06] p-5 bg-[#080808]">
              <Field label="Label" value={s.label} onChange={(v) => update(["stats", i, "label"], v)} />
              <Field label="Wert" value={s.value} onChange={(v) => update(["stats", i, "value"], v)} />
              <Field label="Notiz" value={s.note} onChange={(v) => update(["stats", i, "note"], v)} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Über Uns">
        <Field label="Eyebrow" value={draft.about?.eyebrow} onChange={(v) => update(["about", "eyebrow"], v)} />
        <Field label="Titel" textarea value={draft.about?.title} onChange={(v) => update(["about", "title"], v)} />
        <Field label="Intro Text" textarea value={draft.about?.intro} onChange={(v) => update(["about", "intro"], v)} />
        <ImageUpload label="Über-Uns Bild" value={draft.about?.image_url} onChange={(v) => update(["about", "image_url"], v)} testid="field-about-image" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Caption Eyebrow" value={draft.about?.caption_eyebrow} onChange={(v) => update(["about", "caption_eyebrow"], v)} />
          <Field label="Caption Text" value={draft.about?.caption_text} onChange={(v) => update(["about", "caption_text"], v)} />
        </div>
        <div className="mt-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Pillars (4 Stück)</div>
          <div className="grid md:grid-cols-2 gap-4">
            {(draft.about?.pillars || []).map((p, i) => (
              <div key={i} className="border border-white/[0.06] p-5 bg-[#080808]">
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Nr." value={p.num} onChange={(v) => update(["about", "pillars", i, "num"], v)} />
                  <div className="col-span-2"><Field label="Titel" value={p.title} onChange={(v) => update(["about", "pillars", i, "title"], v)} /></div>
                </div>
                <Field label="Text" textarea value={p.text} onChange={(v) => update(["about", "pillars", i, "text"], v)} />
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section title="Leistungen">
        <div className="grid md:grid-cols-2 gap-4">
          {(draft.services || []).map((s, i) => (
            <div key={i} className="border border-white/[0.06] p-5 bg-[#080808]">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Code" value={s.code} onChange={(v) => update(["services", i, "code"], v)} />
                <div className="col-span-2"><Field label="Titel" value={s.title} onChange={(v) => update(["services", i, "title"], v)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Preis" value={s.price} onChange={(v) => update(["services", i, "price"], v)} />
                <Field label="Sub" value={s.sub} onChange={(v) => update(["services", i, "sub"], v)} />
              </div>
              <Field label="Beschreibung" textarea value={s.description} onChange={(v) => update(["services", i, "description"], v)} />
              <Field label="Bullets (Komma-getrennt)" value={(s.bullets || []).join(", ")} onChange={(v) => update(["services", i, "bullets"], v.split(",").map((x) => x.trim()).filter(Boolean))} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="CTA Button-Text" value={s.cta} onChange={(v) => update(["services", i, "cta"], v)} />
                <Field label="Verlinkung (z.B. /studio — leer = Kontakt)" value={s.link} onChange={(v) => update(["services", i, "link"], v)} />
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.services = c.services || []; c.services.push({ code: String((c.services.length || 0) + 1).padStart(2, "0"), title: "NEUE LEISTUNG", price: "ab 0 €", sub: "/ Paket", description: "", bullets: [], cta: "", link: "" }); return c; }); }} className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]" data-testid="add-service">
          <Plus size={12} /> Leistung hinzufügen
        </button>
      </Section>

      <Section title="Künstler">
        <div className="grid md:grid-cols-2 gap-4">
          {(draft.artists || []).map((a, i) => (
            <div key={i} className="border border-white/[0.06] p-5 bg-[#080808]">
              <div className="flex justify-between items-start mb-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Künstler {i + 1}</div>
                <button onClick={() => { if (window.confirm("Künstler entfernen?")) { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.artists.splice(i, 1); return c; }); } }} className="text-zinc-500 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
              <Field label="Name" value={a.name} onChange={(v) => update(["artists", i, "name"], v)} />
              <Field label="Slug (URL — z.B. nono)" value={a.slug} onChange={(v) => update(["artists", i, "slug"], v)} />
              <Field label="Tag (Genre / Stadt)" value={a.tag} onChange={(v) => update(["artists", i, "tag"], v)} />
              <Field label="Rolle / Badge (z.B. Gründer · CEO) — leer lassen wenn keine" value={a.role} onChange={(v) => update(["artists", i, "role"], v)} />
              <ImageUpload label="Bild" value={a.image} onChange={(v) => update(["artists", i, "image"], v)} testid={`field-artist-image-${i}`} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Instagram Handle" value={a.instagram_handle} onChange={(v) => update(["artists", i, "instagram_handle"], v)} />
                <Field label="Instagram URL" value={a.instagram_url} onChange={(v) => update(["artists", i, "instagram_url"], v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Musik-Link Label" value={a.music_label} onChange={(v) => update(["artists", i, "music_label"], v)} />
                <Field label="Musik URL (Spotify / YT / Apple)" value={a.music_url} onChange={(v) => update(["artists", i, "music_url"], v)} />
              </div>
              <PersonExtras type="artists" i={i} p={a} update={update} setDraft={setDraft} />
            </div>
          ))}
        </div>
        <button onClick={() => { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.artists = c.artists || []; c.artists.push({ name: "NEW", tag: "Genre / Stadt", role: "", image: "", instagram_handle: "", instagram_url: "", music_label: "Aktuelle Songs", music_url: "" }); return c; }); }} className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]" data-testid="add-artist">
          <Plus size={12} /> Künstler hinzufügen
        </button>
      </Section>

      <Section title="Creator">
        <div className="grid md:grid-cols-2 gap-4">
          {(draft.creators || []).map((c, i) => (
            <div key={i} className="border border-white/[0.06] p-5 bg-[#080808]">
              <div className="flex justify-between items-start mb-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Creator {i + 1}</div>
                <button onClick={() => { if (window.confirm("Creator entfernen?")) { setDraft((d) => { const co = JSON.parse(JSON.stringify(d)); co.creators.splice(i, 1); return co; }); } }} className="text-zinc-500 hover:text-red-500" data-testid={`del-creator-${i}`}><Trash2 size={14} /></button>
              </div>
              <Field label="Anzeige-Name (z.B. SCHABROWSKI)" value={c.name} onChange={(v) => update(["creators", i, "name"], v)} />
              <Field label="Slug (URL-Teil, kleingeschrieben, keine Umlaute)" value={c.slug} onChange={(v) => update(["creators", i, "slug"], v)} />
              <Field label="Tag (z.B. Content Creator · Berlin)" value={c.tag} onChange={(v) => update(["creators", i, "tag"], v)} />
              <Field label="Rolle / Badge (optional)" value={c.role} onChange={(v) => update(["creators", i, "role"], v)} />
              <ImageUpload label="Bild (Portrait, hochformat 3:4)" value={c.image} onChange={(v) => update(["creators", i, "image"], v)} testid={`field-creator-image-${i}`} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Instagram Handle" value={c.instagram_handle} onChange={(v) => update(["creators", i, "instagram_handle"], v)} />
                <Field label="Instagram URL" value={c.instagram_url} onChange={(v) => update(["creators", i, "instagram_url"], v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="YouTube-Link Label" value={c.youtube_label} onChange={(v) => update(["creators", i, "youtube_label"], v)} />
                <Field label="YouTube / TikTok URL" value={c.youtube_url} onChange={(v) => update(["creators", i, "youtube_url"], v)} />
              </div>
              <PersonExtras type="creators" i={i} p={c} update={update} setDraft={setDraft} />

              <div className="mt-6 pt-4 border-t border-white/[0.06]">
                <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Impressum-Daten (unter /creator/{c.slug || "…"}/impressum)</div>
                <Field label="Voller Name" value={c.impressum_name} onChange={(v) => update(["creators", i, "impressum_name"], v)} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Straße + Nr." value={c.impressum_address} onChange={(v) => update(["creators", i, "impressum_address"], v)} />
                  <Field label="PLZ + Ort" value={c.impressum_zip_city} onChange={(v) => update(["creators", i, "impressum_zip_city"], v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Land" value={c.impressum_country} onChange={(v) => update(["creators", i, "impressum_country"], v)} />
                  <Field label="E-Mail (Creator)" value={c.impressum_email} onChange={(v) => update(["creators", i, "impressum_email"], v)} />
                </div>
                <div className="mt-3">
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Freitext-Impressum (Markdown, überschreibt Standard)</label>
                  <textarea
                    value={c.impressum || ""}
                    onChange={(e) => update(["creators", i, "impressum"], e.target.value)}
                    rows={6}
                    placeholder="Leer lassen, um den automatischen Impressum-Text aus den Feldern oben zu nutzen."
                    className="w-full bg-[#050505] border border-white/10 px-3 py-2 text-white text-sm outline-none focus:border-[#ff0033]/60 font-mono"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.creators = c.creators || []; c.creators.push({ name: "NEW CREATOR", slug: "new-creator", tag: "Content Creator · Berlin", role: "", image: "", instagram_handle: "", instagram_url: "", youtube_label: "Aktuelle Videos", youtube_url: "", impressum_name: "", impressum_address: "", impressum_zip_city: "", impressum_country: "Deutschland", impressum_email: "", impressum: "", impressum_updated: "" }); return c; }); }} className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]" data-testid="add-creator">
          <Plus size={12} /> Creator hinzufügen
        </button>
      </Section>

      <Section title="Produzenten">
        <div className="grid md:grid-cols-2 gap-4">
          {(draft.producers || []).map((p, i) => (
            <div key={i} className="border border-white/[0.06] p-5 bg-[#080808]">
              <div className="flex justify-between items-start mb-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Produzent {i + 1}</div>
                <button onClick={() => { if (window.confirm("Produzent entfernen?")) { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.producers.splice(i, 1); return c; }); } }} className="text-zinc-500 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
              <Field label="Name" value={p.name} onChange={(v) => update(["producers", i, "name"], v)} />
              <Field label="Slug (URL)" value={p.slug} onChange={(v) => update(["producers", i, "slug"], v)} />
              <Field label="Tag (Genre / Stadt)" value={p.tag} onChange={(v) => update(["producers", i, "tag"], v)} />
              <Field label="Rolle / Badge (z.B. Gründer · CEO · Geschäftsführer) — leer lassen wenn keine" value={p.role} onChange={(v) => update(["producers", i, "role"], v)} />
              <ImageUpload label="Bild" value={p.image} onChange={(v) => update(["producers", i, "image"], v)} testid={`field-producer-image-${i}`} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Instagram Handle" value={p.instagram_handle} onChange={(v) => update(["producers", i, "instagram_handle"], v)} />
                <Field label="Instagram URL" value={p.instagram_url} onChange={(v) => update(["producers", i, "instagram_url"], v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Musik-Link Label" value={p.music_label} onChange={(v) => update(["producers", i, "music_label"], v)} />
                <Field label="Musik URL (Spotify / YT / Apple)" value={p.music_url} onChange={(v) => update(["producers", i, "music_url"], v)} />
              </div>
              <PersonExtras type="producers" i={i} p={p} update={update} setDraft={setDraft} />
            </div>
          ))}
        </div>
        <button onClick={() => { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.producers = c.producers || []; c.producers.push({ name: "NEW", tag: "Genre / Stadt", role: "", image: "", instagram_handle: "", instagram_url: "", music_label: "Beats / Releases", music_url: "" }); return c; }); }} className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]" data-testid="add-producer">
          <Plus size={12} /> Produzent hinzufügen
        </button>
      </Section>

      <Section title="Featured Releases (Now Playing Block)">
        <div className="mb-6 text-xs text-zinc-500 leading-relaxed">
          Hier verwaltest du eure Releases. Wenn die Liste leer ist, wird auf der Website automatisch der "Coming Soon"-Block mit dem Text unten angezeigt.
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {(draft.releases || []).map((r, i) => (
            <div key={i} className="border border-white/[0.06] p-5 bg-[#080808]">
              <div className="flex justify-between items-start mb-3">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Release {i + 1}</div>
                <button onClick={() => { if (window.confirm("Release entfernen?")) { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.releases.splice(i, 1); return c; }); } }} className="text-zinc-500 hover:text-red-500" data-testid={`release-delete-${i}`}><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Artist" value={r.artist} onChange={(v) => update(["releases", i, "artist"], v)} />
                <Field label="Titel" value={r.title} onChange={(v) => update(["releases", i, "title"], v)} />
              </div>
              <Field label="Beschreibung" textarea value={r.description} onChange={(v) => update(["releases", i, "description"], v)} />
              <ImageUpload label="Cover" value={r.cover} onChange={(v) => update(["releases", i, "cover"], v)} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Release Datum (YYYY-MM-DD)" value={r.release_date} onChange={(v) => update(["releases", i, "release_date"], v)} />
                <Field label="Format" value={r.format} onChange={(v) => update(["releases", i, "format"], v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Label" value={r.label} onChange={(v) => update(["releases", i, "label"], v)} />
                <Field label="Badge Text" value={r.badge} onChange={(v) => update(["releases", i, "badge"], v)} />
              </div>
              <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Streaming Links</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Spotify" value={r.links?.spotify} onChange={(v) => update(["releases", i, "links", "spotify"], v)} />
                <Field label="Apple Music" value={r.links?.apple} onChange={(v) => update(["releases", i, "links", "apple"], v)} />
                <Field label="Amazon Music" value={r.links?.amazon} onChange={(v) => update(["releases", i, "links", "amazon"], v)} />
                <Field label="YouTube" value={r.links?.youtube} onChange={(v) => update(["releases", i, "links", "youtube"], v)} />
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.releases = c.releases || []; c.releases.push({ artist: "", title: "Neuer Release", description: "", cover: "", release_date: "", format: "Single", label: "STRAßENWERT", badge: "Out Now", links: { spotify: "", apple: "", amazon: "", youtube: "" } }); return c; }); }} className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]" data-testid="add-release">
          <Plus size={12} /> Release hinzufügen
        </button>

        <div className="mt-10 pt-8 border-t border-white/[0.06]">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-4">Texte (Coming Soon Zustand)</div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Headline Zeile 1" value={draft.release_meta?.empty_headline_a} onChange={(v) => update(["release_meta", "empty_headline_a"], v)} />
            <Field label="Headline Zeile 2 (rot)" value={draft.release_meta?.empty_headline_b} onChange={(v) => update(["release_meta", "empty_headline_b"], v)} />
          </div>
          <Field label="Intro-Text rechts" textarea value={draft.release_meta?.empty_intro} onChange={(v) => update(["release_meta", "empty_intro"], v)} />
          <Field label="Beschreibung im Block" textarea value={draft.release_meta?.empty_body} onChange={(v) => update(["release_meta", "empty_body"], v)} />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Badge" value={draft.release_meta?.empty_badge} onChange={(v) => update(["release_meta", "empty_badge"], v)} />
            <Field label="Status" value={draft.release_meta?.empty_status} onChange={(v) => update(["release_meta", "empty_status"], v)} />
            <Field label="ETA" value={draft.release_meta?.empty_eta} onChange={(v) => update(["release_meta", "empty_eta"], v)} />
          </div>
        </div>
      </Section>

      <Section title="Studios (Coming Soon Panel)">
        <Field label="Eyebrow" value={draft.studios?.eyebrow} onChange={(v) => update(["studios", "eyebrow"], v)} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Titel Teil A" value={draft.studios?.title_a} onChange={(v) => update(["studios", "title_a"], v)} />
          <Field label="Titel Teil B" value={draft.studios?.title_b} onChange={(v) => update(["studios", "title_b"], v)} />
          <Field label="Titel Teil C" value={draft.studios?.title_c} onChange={(v) => update(["studios", "title_c"], v)} />
        </div>
        <Field label="Intro Text" textarea value={draft.studios?.intro} onChange={(v) => update(["studios", "intro"], v)} />
        <div className="grid grid-cols-4 gap-3">
          <Field label="Build Year" value={draft.studios?.build_year} onChange={(v) => update(["studios", "build_year"], v)} />
          <Field label="Phase" value={draft.studios?.phase} onChange={(v) => update(["studios", "phase"], v)} />
          <Field label="Standort" value={draft.studios?.location} onChange={(v) => update(["studios", "location"], v)} />
          <Field label="Launch" value={draft.studios?.launch} onChange={(v) => update(["studios", "launch"], v)} />
        </div>
        <div className="mt-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Instagram Handles</div>
          {(draft.studios?.handles || []).map((h, i) => (
            <div key={i} className="grid grid-cols-2 gap-3 mb-2">
              <Field label={`Handle ${i + 1}`} value={h.tag} onChange={(v) => update(["studios", "handles", i, "tag"], v)} />
              <Field label="URL" value={h.url} onChange={(v) => update(["studios", "handles", i, "url"], v)} />
            </div>
          ))}
        </div>
        <div className="mt-6 pt-6 border-t border-white/[0.06]">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">Texte im rechten Block (Mobile Studio Teaser)</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Linker Block · Status Label" value={draft.studios?.status_label} onChange={(v) => update(["studios", "status_label"], v)} />
            <Field label="Linker Block · Großes Wort (z.B. Build)" value={draft.studios?.big_word} onChange={(v) => update(["studios", "big_word"], v)} />
            <Field label="Rechter Block · Eyebrow" value={draft.studios?.right_eyebrow} onChange={(v) => update(["studios", "right_eyebrow"], v)} />
            <Field label="CTA Button-Text" value={draft.studios?.cta_label} onChange={(v) => update(["studios", "cta_label"], v)} />
            <Field label="Rechter Block · Headline Teil A" value={draft.studios?.right_title_a} onChange={(v) => update(["studios", "right_title_a"], v)} />
            <Field label="Rechter Block · Headline Teil B (rot)" value={draft.studios?.right_title_b} onChange={(v) => update(["studios", "right_title_b"], v)} />
          </div>
          <Field label="Rechter Block · Beschreibung" textarea value={draft.studios?.right_body} onChange={(v) => update(["studios", "right_body"], v)} />
        </div>
      </Section>

      <Section title="Mobile Studio (Seite /studio)">
        <div className="mb-4 text-xs text-zinc-500 leading-relaxed border border-[#ff0033]/30 bg-[#ff0033]/[0.04] p-4">
          Eigene Seite unter <code className="text-[#ff0033]">/studio</code> mit Hero, Leistungen und Anfrageformular für mobile Studio-Sessions. Anfragen landen im Admin-Tab <strong>"Studio Buchungen"</strong>.
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Eyebrow / Kicker" value={draft.studio_page?.eyebrow} onChange={(v) => update(["studio_page", "eyebrow"], v)} />
          <div />
          <Field label="Headline Zeile 1" value={draft.studio_page?.title_a} onChange={(v) => update(["studio_page", "title_a"], v)} />
          <Field label="Headline Zeile 2 (rot)" value={draft.studio_page?.title_b} onChange={(v) => update(["studio_page", "title_b"], v)} />
        </div>
        <Field label="Intro Text" textarea value={draft.studio_page?.intro} onChange={(v) => update(["studio_page", "intro"], v)} />

        <div className="mt-6 pt-6 border-t border-white/[0.06]">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-4">Studio-Leistungen</div>
          <div className="grid md:grid-cols-2 gap-4">
            {(draft.studio_page?.services || []).map((s, i) => (
              <div key={i} className="border border-white/[0.06] p-5 bg-[#080808]">
                <div className="flex justify-between items-start mb-3">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">Leistung {i + 1}</div>
                  <button onClick={() => { if (window.confirm("Leistung entfernen?")) { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.studio_page.services.splice(i, 1); return c; }); } }} className="text-zinc-500 hover:text-red-500" data-testid={`studio-svc-del-${i}`}><Trash2 size={14} /></button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Code" value={s.code} onChange={(v) => update(["studio_page", "services", i, "code"], v)} />
                  <div className="col-span-2"><Field label="Titel" value={s.title} onChange={(v) => update(["studio_page", "services", i, "title"], v)} /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Preis" value={s.price} onChange={(v) => update(["studio_page", "services", i, "price"], v)} />
                  <Field label="Sub" value={s.sub} onChange={(v) => update(["studio_page", "services", i, "sub"], v)} />
                </div>
                <Field label="Beschreibung" textarea value={s.description} onChange={(v) => update(["studio_page", "services", i, "description"], v)} />
                <Field label="CTA Button-Text" value={s.cta} onChange={(v) => update(["studio_page", "services", i, "cta"], v)} />
              </div>
            ))}
          </div>
          <button onClick={() => { setDraft((d) => { const c = JSON.parse(JSON.stringify(d)); c.studio_page = c.studio_page || {}; c.studio_page.services = c.studio_page.services || []; c.studio_page.services.push({ code: String((c.studio_page.services.length || 0) + 1).padStart(2, "0"), title: "Neue Leistung", price: "ab 0 €", sub: "/ Session", description: "", cta: "Session anfragen" }); return c; }); }} className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]" data-testid="add-studio-service">
            <Plus size={12} /> Studio-Leistung hinzufügen
          </button>
        </div>

        <div className="mt-8 pt-6 border-t border-white/[0.06]">
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-4">Texte über den Leistungen & Formular</div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Leistungen-Headline" value={draft.studio_page?.services_title} onChange={(v) => update(["studio_page", "services_title"], v)} />
            <Field label="Leistungen-Intro" value={draft.studio_page?.services_intro} onChange={(v) => update(["studio_page", "services_intro"], v)} />
            <Field label="Formular-Headline" value={draft.studio_page?.form_title} onChange={(v) => update(["studio_page", "form_title"], v)} />
            <Field label="Formular-Intro" value={draft.studio_page?.form_intro} onChange={(v) => update(["studio_page", "form_intro"], v)} />
          </div>
        </div>
      </Section>

      <Section title="Kontakt">
        <div className="grid grid-cols-2 gap-4">
          <Field label="E-Mail" value={draft.contact?.email} onChange={(v) => update(["contact", "email"], v)} testid="field-contact-email" />
          <Field label="Telefon (Anzeige)" value={draft.contact?.phone} onChange={(v) => update(["contact", "phone"], v)} testid="field-contact-phone" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Telefon (Link ohne Leerzeichen)" value={draft.contact?.phone_link} onChange={(v) => update(["contact", "phone_link"], v)} />
          <Field label="Standort" value={draft.contact?.location} onChange={(v) => update(["contact", "location"], v)} />
        </div>
        <Field label="Intro Text" textarea value={draft.contact?.intro} onChange={(v) => update(["contact", "intro"], v)} />
      </Section>

      <Section title="Unsere Geschichte (Seite /geschichte)">
        <div className="mb-4 text-xs text-zinc-500 leading-relaxed border border-[#ff0033]/30 bg-[#ff0033]/[0.04] p-4">
          Versteckte Seite mit deiner Vision &amp; Story als Gründer. Erreichbar über{" "}
          <code className="text-[#ff0033]">/geschichte</code> · Footer-Link &amp; kleiner Hinweis im "Über Uns"-Block. Markdown im Story-Text:{" "}
          <code className="text-[#ff0033]">## Überschrift</code>,{" "}
          <code className="text-[#ff0033]">**fett**</code>,{" "}
          <code className="text-[#ff0033]">- Listenpunkt</code>.
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Eyebrow / Kicker" value={draft.story?.eyebrow} onChange={(v) => update(["story", "eyebrow"], v)} />
          <div />
          <Field label="Headline Zeile 1" value={draft.story?.title_a} onChange={(v) => update(["story", "title_a"], v)} />
          <Field label="Headline Zeile 2 (rot glow)" value={draft.story?.title_b} onChange={(v) => update(["story", "title_b"], v)} />
        </div>
        <Field label="Untertitel (optional)" textarea value={draft.story?.subtitle} onChange={(v) => update(["story", "subtitle"], v)} />

        <ImageUpload label="Portrait Bild (du als Gründer)" value={draft.story?.portrait_url} onChange={(v) => update(["story", "portrait_url"], v)} testid="field-story-portrait" />

        <div className="grid grid-cols-2 gap-4 mt-2">
          <Field label="Name (unter Portrait)" value={draft.story?.author_name} onChange={(v) => update(["story", "author_name"], v)} />
          <Field label="Rolle (z.B. Gründer)" value={draft.story?.author_role} onChange={(v) => update(["story", "author_role"], v)} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Pull-Quote (optional, dezent prominent)" textarea value={draft.story?.quote} onChange={(v) => update(["story", "quote"], v)} />
          <Field label="Quote-Attribution" value={draft.story?.quote_attribution} onChange={(v) => update(["story", "quote_attribution"], v)} />
        </div>

        <label className="block mt-2">
          <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Story-Text (Markdown)</span>
          <textarea
            value={draft.story?.body ?? ""}
            onChange={(e) => update(["story", "body"], e.target.value)}
            rows={20}
            data-testid="field-story-body"
            placeholder="Hier deine Vision, Geschichte, wie alles begann…&#10;&#10;## Wie alles begann&#10;Erster Absatz…"
            className="w-full bg-[#050505] border border-white/10 focus:border-[#ff0033]/60 outline-none text-white text-sm p-3 font-mono leading-relaxed resize-y transition-colors"
          />
        </label>

        <Field label="Sign-off (Schlussformel)" value={draft.story?.signoff} onChange={(v) => update(["story", "signoff"], v)} />
      </Section>

      <Section title="Rechtliches (Impressum · Datenschutz · AGB)">
        <div className="mb-6 text-xs text-zinc-500 leading-relaxed border border-[#ff0033]/30 bg-[#ff0033]/[0.04] p-4">
          <strong className="text-white">Einfache Formatierung:</strong> Verwende{" "}
          <code className="text-[#ff0033]">## Überschrift</code> für Hauptpunkte,{" "}
          <code className="text-[#ff0033]">### Unterüberschrift</code> für Sub-Sektionen,{" "}
          <code className="text-[#ff0033]">- Punkt</code> für Listen,{" "}
          <code className="text-[#ff0033]">**fett**</code> für fetten Text,{" "}
          <code className="text-[#ff0033]">[Linktext](https://...)</code> für Links,{" "}
          <code className="text-[#ff0033]">---</code> für eine Trennlinie. Leerzeile = neuer Absatz.
        </div>

        <div className="space-y-10">
          {[
            { key: "impressum", title: "Impressum", default: DEFAULT_IMPRESSUM, route: "/impressum" },
            { key: "datenschutz", title: "Datenschutz", default: DEFAULT_DATENSCHUTZ, route: "/datenschutz" },
            { key: "agb", title: "AGB", default: DEFAULT_AGB, route: "/agb" },
          ].map((page) => (
            <div key={page.key} className="border border-white/[0.06] p-5 bg-[#080808]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold">{page.title}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 mt-1">
                    URL: <code className="text-zinc-400">{page.route}</code>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`${page.title} auf Standardtext zurücksetzen? Deine Änderungen gehen verloren.`)) {
                      update(["legal", page.key, "body"], page.default);
                    }
                  }}
                  data-testid={`legal-reset-${page.key}`}
                  className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase border border-white/15 px-3 py-2 hover:border-white/30 text-zinc-400 hover:text-white"
                >
                  <RotateCcw size={11} /> Standard
                </button>
              </div>

              <Field
                label="Stand (z.B. Mai 2026)"
                value={draft.legal?.[page.key]?.updated}
                onChange={(v) => update(["legal", page.key, "updated"], v)}
              />

              <label className="block">
                <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Inhalt (Markdown)</span>
                <textarea
                  value={draft.legal?.[page.key]?.body ?? ""}
                  onChange={(e) => update(["legal", page.key, "body"], e.target.value)}
                  rows={18}
                  data-testid={`legal-textarea-${page.key}`}
                  className="w-full bg-[#050505] border border-white/10 focus:border-[#ff0033]/60 outline-none text-white text-sm p-3 font-mono leading-relaxed resize-y transition-colors"
                />
              </label>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mb-14">
    <h3 className="font-display font-black uppercase tracking-tight text-2xl md:text-3xl mb-6 pb-3 border-b border-white/10">{title}</h3>
    {children}
  </section>
);

const Field = ({ label, value = "", onChange, textarea, testid, type = "text" }) => (
  <label className="block mb-4">
    <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">{label}</span>
    {textarea ? (
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} data-testid={testid} className="w-full bg-[#050505] border border-white/10 focus:border-[#ff0033]/60 outline-none text-white text-sm p-3 font-medium resize-y transition-colors" />
    ) : (
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full bg-[#050505] border border-white/10 focus:border-[#ff0033]/60 outline-none text-white text-sm p-3 font-medium transition-colors" />
    )}
  </label>
);

// Wiederverwendbare "Detailseite"-Felder für Künstler / Creator / Producer:
// Bio + Kanäle (Follower) + Songs (Streams) + Partnerschaften. Alle als Sub-Listen
// mit Add/Delete-Buttons. Speichert direkt in draft.[type][i].{bio,channels,songs,partners}.
const PersonExtras = ({ type, i, p, update, setDraft }) => {
  const channels = p.channels || [];
  const songs = p.songs || [];
  const partners = p.partners || [];
  const events = p.events || [];

  const addItem = (key, item) => setDraft((d) => {
    const c = JSON.parse(JSON.stringify(d));
    c[type][i][key] = c[type][i][key] || [];
    c[type][i][key].push(item);
    return c;
  });
  const delItem = (key, idx) => setDraft((d) => {
    const c = JSON.parse(JSON.stringify(d));
    c[type][i][key].splice(idx, 1);
    return c;
  });

  return (
    <div className="mt-6 pt-4 border-t border-white/[0.06] space-y-6">
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={!!p.verified} onChange={(e) => update([type, i, "verified"], e.target.checked)} className="w-4 h-4 accent-[#ff0033]" />
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">Verifiziert-Badge anzeigen (roter Haken neben Namen)</span>
      </label>

      <label className="block">
        <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-2">Bio (auf der Detailseite unter dem Namen)</span>
        <textarea value={p.bio || ""} onChange={(e) => update([type, i, "bio"], e.target.value)} rows={3} className="w-full bg-[#050505] border border-white/10 focus:border-[#ff0033]/60 outline-none text-white text-sm p-3 font-medium resize-y" />
      </label>

      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-3">Kanäle & Followerzahlen</div>
        {channels.map((ch, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-[130px_1fr_1fr_130px_auto] gap-2 mb-2 items-center">
            <select value={ch.platform || "instagram"} onChange={(e) => update([type, i, "channels", idx, "platform"], e.target.value)} className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-2">
              <option value="instagram">Instagram</option>
              <option value="tiktok">TikTok</option>
              <option value="youtube">YouTube</option>
              <option value="twitch">Twitch</option>
              <option value="spotify">Spotify</option>
              <option value="apple">Apple Music</option>
              <option value="soundcloud">SoundCloud</option>
              <option value="x">X / Twitter</option>
              <option value="facebook">Facebook</option>
              <option value="other">Sonstiges</option>
            </select>
            <input value={ch.handle || ""} onChange={(e) => update([type, i, "channels", idx, "handle"], e.target.value)} placeholder="Handle (@name)" className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-2" />
            <input value={ch.url || ""} onChange={(e) => update([type, i, "channels", idx, "url"], e.target.value)} placeholder="URL" className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-2" />
            <input value={ch.followers || ""} onChange={(e) => update([type, i, "channels", idx, "followers"], e.target.value)} placeholder="z.B. 12.5K" className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-2" />
            <button onClick={() => delItem("channels", idx)} className="text-zinc-500 hover:text-red-500"><Trash2 size={13} /></button>
          </div>
        ))}
        <button onClick={() => addItem("channels", { platform: "instagram", handle: "", url: "", followers: "" })} className="text-[10px] tracking-[0.3em] uppercase border border-white/15 px-3 py-1.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]"><Plus size={11} className="inline mr-1" /> Kanal</button>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-3">Songs & Streams</div>
        {songs.map((s, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_130px_130px_1fr_auto] gap-2 mb-2 items-center">
            <input value={s.title || ""} onChange={(e) => update([type, i, "songs", idx, "title"], e.target.value)} placeholder="Titel" className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-2" />
            <input value={s.streams || ""} onChange={(e) => update([type, i, "songs", idx, "streams"], e.target.value)} placeholder="Streams (z.B. 1.2M)" className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-2" />
            <input value={s.released || ""} onChange={(e) => update([type, i, "songs", idx, "released"], e.target.value)} placeholder="Release-Datum" className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-2" />
            <input value={s.url || ""} onChange={(e) => update([type, i, "songs", idx, "url"], e.target.value)} placeholder="Link (Spotify/YT)" className="bg-[#050505] border border-white/10 text-white text-xs px-2 py-2" />
            <button onClick={() => delItem("songs", idx)} className="text-zinc-500 hover:text-red-500"><Trash2 size={13} /></button>
          </div>
        ))}
        <button onClick={() => addItem("songs", { title: "", streams: "", released: "", url: "", cover: "" })} className="text-[10px] tracking-[0.3em] uppercase border border-white/15 px-3 py-1.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]"><Plus size={11} className="inline mr-1" /> Song</button>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-3">Partnerschaften & Kooperationen</div>
        {partners.map((pt, idx) => (
          <div key={idx} className="border border-white/10 bg-[#050505] p-3 mb-3 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_130px_130px_auto] gap-2 items-start">
              <input value={pt.name || ""} onChange={(e) => update([type, i, "partners", idx, "name"], e.target.value)} placeholder="Marke / Firma" className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" />
              <input value={pt.type || ""} onChange={(e) => update([type, i, "partners", idx, "type"], e.target.value)} placeholder="Typ (Sponsor)" className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" />
              <input value={pt.since || ""} onChange={(e) => update([type, i, "partners", idx, "since"], e.target.value)} placeholder="seit (Jahr)" className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" />
              <button onClick={() => delItem("partners", idx)} className="text-zinc-500 hover:text-red-500 px-2"><Trash2 size={13} /></button>
            </div>
            <input value={pt.description || ""} onChange={(e) => update([type, i, "partners", idx, "description"], e.target.value)} placeholder="Beschreibung (z.B. Premium Gaming Booster – für mehr Fokus und Performance)" className="w-full bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_130px] gap-2">
              <input value={pt.url || ""} onChange={(e) => update([type, i, "partners", idx, "url"], e.target.value)} placeholder="Shop-URL (öffnet in neuem Tab)" className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" />
              <input value={pt.cta_label || ""} onChange={(e) => update([type, i, "partners", idx, "cta_label"], e.target.value)} placeholder="Button-Text (Zum Shop)" className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" />
            </div>
            <ImageUpload label="Logo (PNG mit Transparenz, sonst wird der Name angezeigt)" value={pt.logo} onChange={(v) => update([type, i, "partners", idx, "logo"], v)} testid={`field-partner-logo-${type}-${i}-${idx}`} />
          </div>
        ))}
        <button onClick={() => addItem("partners", { name: "", type: "Kooperation", since: "", description: "", url: "", cta_label: "Zum Shop", logo: "" })} className="text-[10px] tracking-[0.3em] uppercase border border-white/15 px-3 py-1.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]"><Plus size={11} className="inline mr-1" /> Partner</button>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#ff0033] font-bold mb-3">Events (Termine)</div>
        <div className="text-[10px] text-zinc-500 mb-3 leading-relaxed">Alle Events werden angezeigt in der Reihenfolge wie hier eingetragen. Manuell löschen zum Entfernen.</div>
        {events.map((ev, idx) => (
          <div key={idx} className="border border-white/10 bg-[#050505] p-3 mb-3 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_150px_auto] gap-2 items-start">
              <input value={ev.title || ""} onChange={(e) => update([type, i, "events", idx, "title"], e.target.value)} placeholder="Event-Titel (z.B. VENUS BERLIN)" className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" data-testid={`field-event-title-${type}-${i}-${idx}`} />
              <input type="date" value={ev.date || ""} onChange={(e) => update([type, i, "events", idx, "date"], e.target.value)} className="bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" data-testid={`field-event-date-${type}-${i}-${idx}`} />
              <button onClick={() => delItem("events", idx)} className="text-zinc-500 hover:text-red-500 px-2" data-testid={`del-event-${type}-${i}-${idx}`}><Trash2 size={13} /></button>
            </div>
            <input value={ev.location || ""} onChange={(e) => update([type, i, "events", idx, "location"], e.target.value)} placeholder="Location / Venue (z.B. Berlin Arena)" className="w-full bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2" data-testid={`field-event-location-${type}-${i}-${idx}`} />
            <textarea value={ev.description || ""} onChange={(e) => update([type, i, "events", idx, "description"], e.target.value)} placeholder="Beschreibung" rows={2} className="w-full bg-[#0A0A0A] border border-white/10 text-white text-xs px-2 py-2 resize-y" />
            <ImageUpload label="Event-Logo (permanent farbig auf der Profilseite)" value={ev.logo} onChange={(v) => update([type, i, "events", idx, "logo"], v)} testid={`field-event-logo-${type}-${i}-${idx}`} />
          </div>
        ))}
        <button onClick={() => addItem("events", { title: "", date: "", location: "", description: "", logo: "" })} className="text-[10px] tracking-[0.3em] uppercase border border-white/15 px-3 py-1.5 hover:border-[#ff0033]/50 hover:text-[#ff0033]" data-testid={`add-event-${type}-${i}`}><Plus size={11} className="inline mr-1" /> Event</button>
      </div>
    </div>
  );
};
